import React, { useState, useEffect } from 'react';
import { Card, Form, Modal, Badge } from 'react-bootstrap';
import Button from "components/base/Button";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilEdit,
  UilTrash,
  UilClipboardNotes
} from '@iconscout/react-unicons';
import classNames from 'classnames';
import SearchBox from 'components/common/SearchBox';
import { useForm, SubmitHandler } from 'react-hook-form';
import { dateFormatter } from 'helpers/utils';
import toast from 'react-hot-toast';
import NotesSkeleton from 'components/common/skeletons/NotesSkeleton';

interface NoteItem {
  id: string;
  note: string;
  created_on: string;
  creator_type?: string;
  creator_id?: string;
}

interface ModernNotesListProps {
  notes: NoteItem[];
  isLoading: boolean;
  onCreateNote: (noteData: any) => Promise<void>;
  onUpdateNote?: (noteId: string, noteData: any) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
}

type NoteFormInputs = {
  note: string;
};

const ModernNotesList: React.FC<ModernNotesListProps> = ({
  notes,
  isLoading,
  onCreateNote,
  onUpdateNote,
  onDeleteNote
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNote, setEditingNote] = useState<NoteItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNotes, setFilteredNotes] = useState<NoteItem[]>(notes);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    reset: resetCreate,
    watch: watchCreate,
    formState: { errors: errorsCreate }
  } = useForm<NoteFormInputs>();

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    reset: resetEdit,
    setValue,
    watch: watchEdit,
    formState: { errors: errorsEdit }
  } = useForm<NoteFormInputs>();

  // Watch form values for character count
  const createNoteContent = watchCreate("note", "");
  const editNoteContent = watchEdit("note", "");

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredNotes(notes);
    } else {
      setFilteredNotes(
        notes.filter(note => 
          note.note.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
  }, [notes, searchQuery]);

  const onSubmitCreate: SubmitHandler<NoteFormInputs> = async (data) => {
    try {
      await onCreateNote(data);
      resetCreate();
      setShowCreateModal(false);
      toast.success('Note created successfully!');
    } catch (error) {
      toast.error('Failed to create note');
    }
  };

  const onSubmitEdit: SubmitHandler<NoteFormInputs> = async (data) => {
    if (!editingNote || !onUpdateNote) return;
    
    try {
      await onUpdateNote(editingNote.id, data);
      resetEdit();
      setShowEditModal(false);
      setEditingNote(null);
      toast.success('Note updated successfully!');
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleEditNote = (note: NoteItem) => {
    setEditingNote(note);
    setValue('note', note.note);
    setShowEditModal(true);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!onDeleteNote) return;
    
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await onDeleteNote(noteId);
        toast.success('Note deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete note');
      }
    }
  };

  // Date formatting is handled by dateFormatter helper from utils



  return (
    <div className="modern-notes-list">
      {/* Notes Header */}
      <div className="notes-header mb-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
          <div className="mb-2 mb-sm-0">
            <h2 className="mb-1 notes-title" style={{ fontSize: '1.375rem' }}>Notes</h2>
            <small className="text-muted d-none d-sm-block">Manage your lead notes efficiently</small>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="note-create-btn"
            startIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            New Note
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 mb-3">
          <SearchBox
            placeholder="Search notes..."
            className="flex-grow-1"
            style={{ width: '100%', maxWidth: "30rem" }}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
          />
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="notes-count-badge" style={{ width: '70px', height: '24px', justifyContent: 'center', borderRadius: '5px' }}>
              {filteredNotes.length} {filteredNotes.length > 1 ? "notes" : "note"}
            </Badge>
          </div>
        </div>
        
        <hr className="my-2" />
      </div>

      {/* Notes Feed */}
      {isLoading ? (
        <NotesSkeleton />
      ) : (
        <div className="notes-feed">
          {filteredNotes.length === 0 ? (
            <Card className="text-center py-5">
              <Card.Body>
                <UilClipboardNotes size="48" className="text-muted mb-3" />
                {searchQuery ? (
                  <>
                    <h5 className="text-muted">No notes found</h5>
                    <p className="text-muted mb-4">Try adjusting your search query</p>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </Button>
                  </>
                ) : (
                  <>
                    <h5 className="text-muted">No notes yet</h5>
                    <p className="text-muted mb-4">Create the first note for this lead</p>
                  </>
                )}
              </Card.Body>
            </Card>
          ) : (
            filteredNotes.map((note, index) => (
              <Card key={note.id} className="mb-3 note-card">
                <Card.Body className="note-card-body">
                  <div className="d-flex flex-column">
                    {/* Note Content */}
                    <div className="note-content flex-grow-1">
                      <div className="d-flex align-items-center mb-3 note-header">
                        <div className="note-icon me-2">
                          <UilClipboardNotes size="20" className="text-primary" />
                        </div>
                        
                        <div className="note-meta flex-grow-1">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <Badge bg="info" className="note-badge d-flex align-items-center">
                                Note
                              </Badge>
                              {index === 0 && (
                                <Badge bg="success" className="d-flex align-items-center">
                                  Latest
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <p className="mb-0 text-muted">{note.note}</p>

                      <div className="note-footer d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
                        <div className="note-author d-flex align-items-center">
                          <small className="text-muted note-date">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            Created {dateFormatter(note.created_on)}
                          </small>
                        </div>
                        
                        <div className="note-actions">
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-decoration-none p-1"
                            onClick={() => handleEditNote(note)}
                          >
                            <UilEdit size="18" />
                          </Button>
                          <Button 
                            variant="link" 
                            size="sm" 
                            className="text-decoration-none text-danger p-1"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <UilTrash size="18" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Create Note Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" className="note-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <UilClipboardNotes size="24" className="text-primary" />
            Create New Note
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitCreate(onSubmitCreate)}>
            <div className="mb-3">
              <Form.Label className="fw-semibold">
                Note Content <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder="Write your note here..."
                className="modern-textarea"
                {...registerCreate("note", { 
                  required: "Note content is required",
                  maxLength: {
                    value: 3000,
                    message: "Note cannot exceed 3000 characters"
                  }
                })}
              />
              <div className="d-flex justify-content-between mt-2">
                <div>
                  {errorsCreate.note && (
                    <small className="text-danger">{errorsCreate.note?.message}</small>
                  )}
                </div>
                <small className={classNames(
                  "text-muted",
                  { "text-warning": createNoteContent.length > 2500 },
                  { "text-danger": createNoteContent.length > 2900 }
                )}>
                  {createNoteContent.length}/3000 characters
                </small>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitCreate(onSubmitCreate)}
            disabled={!createNoteContent.trim()}
          >
            Create Note
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Note Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" className="note-modal">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <UilEdit size="24" className="text-primary" />
            Edit Note
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitEdit(onSubmitEdit)}>
            <div className="mb-3">
              <Form.Label className="fw-semibold">
                Note Content <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder="Write your note here..."
                className="modern-textarea"
                {...registerEdit("note", { 
                  required: "Note content is required",
                  maxLength: {
                    value: 3000,
                    message: "Note cannot exceed 3000 characters"
                  }
                })}
              />
              <div className="d-flex justify-content-between mt-2">
                <div>
                  {errorsEdit.note && (
                    <small className="text-danger">{errorsEdit.note?.message}</small>
                  )}
                </div>
                <small className={classNames(
                  "text-muted",
                  { "text-warning": editNoteContent.length > 2500 },
                  { "text-danger": editNoteContent.length > 2900 }
                )}>
                  {editNoteContent.length}/3000 characters
                </small>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmitEdit(onSubmitEdit)}
            disabled={!editNoteContent.trim()}
          >
            Update Note
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ModernNotesList; 